import type {Call, Device, TwilioError} from '@twilio/voice-sdk';
import {match} from 'ts-pattern';

import {isNil, isNotNil, without} from 'ramda';
import {noop} from 'ramda-adjunct';

import {BrowserPhoneFailure} from '../types/BrowserPhoneFailure';

/**
 * `unknown` until the first token request answers; `unavailable` when this tab cannot call from the
 * browser (tenant not provisioned, no permission, the token request failed or the token died).
 */
export type BrowserPhoneAvailability = 'unknown' | 'available' | 'unavailable';

export interface BrowserPhoneState {
  availability: BrowserPhoneAvailability;
  /** The call whose audio runs in this tab: an outbound browser call or an accepted inbound one. */
  activeCall: {callId: string; isMuted: boolean} | null;
  /** Omnetic ids of the inbound calls ringing this tab. */
  ringingCallIds: string[];
}

/**
 * What the phone needs from the app. `fetchToken` resolves null when browser calling is off for
 * good (not provisioned, no permission) and rejects on a transient failure, which a later
 * `retryIfUnavailable` repeats.
 */
export interface BrowserPhoneSession {
  fetchToken: () => Promise<string | null>;
  cancelCall: (callId: string) => void;
  onFailure: (failure: BrowserPhoneFailure) => void;
  /** An inbound call rings this tab; the indicator may not know the call yet. */
  onIncoming: VoidFunction;
}

const MICROPHONE_PERMISSION_DENIED = 31401;
const MICROPHONE_ACQUISITION_FAILED = 31402;

/** Invalid or expired Access Token: the Device can neither register nor place calls any more. */
const TOKEN_ERROR_CODES = [20101, 20104, 31204, 31205];

const INITIAL_STATE: BrowserPhoneState = {
  availability: 'unknown',
  activeCall: null,
  ringingCallIds: [],
};

const getFailure = (error: TwilioError.TwilioError): BrowserPhoneFailure =>
  match(error.code)
    .with(MICROPHONE_PERMISSION_DENIED, () => 'MICROPHONE_BLOCKED' as const)
    .with(MICROPHONE_ACQUISITION_FAILED, () => 'NO_MICROPHONE' as const)
    .otherwise(() => 'CONNECTION_FAILED' as const);

/**
 * The tab's softphone: one Twilio Voice SDK `Device` per signed-in tab, registered for incoming
 * calls, plus the SDK `Call` objects keyed by Omnetic call id. The SDK draws nothing; this object
 * only exposes a small state for the UI and the actions on it. The SDK is loaded only once the
 * backend hands out a token, so users without browser calling never download it.
 *
 * The backend stays the source of the call state (indicator, other tabs, colleagues); this state
 * only says which calls this tab can control.
 */
export function createBrowserPhone() {
  let state = INITIAL_STATE;
  let session: BrowserPhoneSession | null = null;
  let device: Device | null = null;
  let isRetryable = false;
  // Bumped on every start/stop, so a token answering after a stop never builds a Device.
  let generation = 0;

  const calls = new Map<string, Call>();
  // Hang-ups asked for while `device.connect()` was still building the call.
  const pendingHangUps = new Set<string>();
  const listeners = new Set<VoidFunction>();

  const setState = (patch: Partial<BrowserPhoneState>) => {
    state = {...state, ...patch};
    listeners.forEach((listener) => listener());
  };

  /** Forgets a call only while `call` is still the one stored for its id (a re-ring replaces it). */
  const forgetCall = (callId: string, call: Call) => {
    if (calls.get(callId) !== call) {
      return;
    }

    calls.delete(callId);
    setState({
      ringingCallIds: without([callId], state.ringingCallIds),
      activeCall: state.activeCall?.callId === callId ? null : state.activeCall,
    });
  };

  /** The token can no longer be trusted: offer no browser calls until the next retry rebuilds it. */
  const markTokenDead = () => {
    isRetryable = true;
    setState({availability: 'unavailable'});
  };

  const handleIncoming = (call: Call) => {
    const callId = call.customParameters.get('callId');

    // Busy in this tab already, or a call we cannot match to the indicator: let the mobile ring.
    if (isNil(callId) || isNotNil(state.activeCall)) {
      call.reject();
      return;
    }

    let isSettled = false;

    // Answered elsewhere, the caller hung up, the user rejected it here, or the call failed.
    const settle = (failure: BrowserPhoneFailure | null) => {
      if (isSettled) {
        return;
      }

      isSettled = true;
      forgetCall(callId, call);

      if (isNotNil(failure)) {
        session?.onFailure(failure);
      }
    };

    calls.set(callId, call);
    setState({ringingCallIds: [...without([callId], state.ringingCallIds), callId]});
    session?.onIncoming();

    call.on('accept', () => {
      if (calls.get(callId) === call) {
        setState({
          ringingCallIds: without([callId], state.ringingCallIds),
          activeCall: {callId, isMuted: false},
        });
      }
    });
    call.on('cancel', () => settle(null));
    call.on('reject', () => settle(null));
    call.on('disconnect', () => settle(null));
    call.on('error', (error: TwilioError.TwilioError) => {
      // Not every SDK error ends the call; end it here so no audio outlives the controls.
      settle(getFailure(error));
      call.disconnect();
    });
  };

  const createDevice = (DeviceClass: typeof Device, token: string) => {
    const nextDevice = new DeviceClass(token, {closeProtection: true});
    // A refresh or error of a Device from an earlier session must not touch the current one.
    const deviceGeneration = generation;
    const isCurrentDevice = () => deviceGeneration === generation;

    nextDevice.on('tokenWillExpire', () =>
      session
        ?.fetchToken()
        .then((nextToken) => {
          if (!isCurrentDevice()) {
            return;
          }

          if (isNil(nextToken)) {
            setState({availability: 'unavailable'});
            return;
          }

          nextDevice.updateToken(nextToken);
        })
        .catch(() => {
          if (isCurrentDevice()) {
            markTokenDead();
          }
        })
    );
    nextDevice.on('incoming', handleIncoming);
    // Registration errors land here too; only a dead token changes what the tab can do.
    nextDevice.on('error', (error: TwilioError.TwilioError) => {
      if (isCurrentDevice() && TOKEN_ERROR_CODES.includes(error.code)) {
        markTokenDead();
      }
    });
    nextDevice.register().catch(noop);

    return nextDevice;
  };

  const loadDevice = () => {
    const currentGeneration = generation;

    session
      ?.fetchToken()
      .then((token) => {
        if (isNil(token)) {
          return null;
        }

        return import('@twilio/voice-sdk').then((sdk) => ({token, DeviceClass: sdk.Device}));
      })
      .then((loaded) => {
        if (currentGeneration !== generation) {
          return;
        }

        isRetryable = false;

        if (isNil(loaded)) {
          setState({availability: 'unavailable'});
          return;
        }

        device = createDevice(loaded.DeviceClass, loaded.token);
        setState({availability: 'available'});
      })
      .catch(() => {
        if (currentGeneration !== generation) {
          return;
        }

        isRetryable = true;
        setState({availability: 'unavailable'});
      });
  };

  const releaseDevice = () => {
    // Destroying the Device hangs up every call it carries.
    device?.destroy();
    device = null;
    calls.clear();
    pendingHangUps.clear();
  };

  const stop = () => {
    generation += 1;
    session = null;
    isRetryable = false;
    releaseDevice();
    setState(INITIAL_STATE);
  };

  const start = (nextSession: BrowserPhoneSession) => {
    stop();
    session = nextSession;
    loadDevice();
  };

  /**
   * A failed token request, or a token that died (refresh failed, laptop slept past the expiry),
   * gets a fresh Device when the user next wants to call. Never while a call runs in this tab:
   * rebuilding would hang it up.
   */
  const retryIfUnavailable = () => {
    if (!isRetryable || isNotNil(state.activeCall)) {
      return;
    }

    isRetryable = false;
    generation += 1;
    releaseDevice();
    setState({...INITIAL_STATE});
    loadDevice();
  };

  /**
   * Connects the tab to an outbound call the backend has just created. Until the SDK reports the
   * call accepted by Twilio, a failure or hang-up cancels the call record, so the indicator does
   * not wait for a call that never reached Twilio.
   */
  const connect = (callId: string) => {
    const currentSession = session;

    if (isNil(device) || isNil(currentSession)) {
      currentSession?.cancelCall(callId);
      currentSession?.onFailure('CONNECTION_FAILED');
      return;
    }

    let hasReachedTwilio = false;
    let isSettled = false;

    const settle = (call: Call | null, failure: BrowserPhoneFailure | null) => {
      if (isSettled) {
        return;
      }

      isSettled = true;
      pendingHangUps.delete(callId);

      if (!hasReachedTwilio) {
        currentSession.cancelCall(callId);
      }

      if (isNotNil(call)) {
        forgetCall(callId, call);
      } else if (state.activeCall?.callId === callId) {
        setState({activeCall: null});
      }

      if (isNotNil(failure)) {
        currentSession.onFailure(failure);
      }
    };

    setState({activeCall: {callId, isMuted: false}});

    device
      .connect({params: {callId}})
      .then((call) => {
        calls.set(callId, call);

        call.on('accept', () => {
          hasReachedTwilio = true;
        });
        call.on('disconnect', () => settle(call, null));
        call.on('error', (error: TwilioError.TwilioError) => {
          // Not every SDK error ends the call; end it here so no audio outlives the controls.
          settle(call, getFailure(error));
          call.disconnect();
        });

        if (pendingHangUps.has(callId)) {
          call.disconnect();
        }
      })
      .catch(() => settle(null, 'CONNECTION_FAILED'));
  };

  const accept = (callId: string) => calls.get(callId)?.accept();

  const reject = (callId: string) => calls.get(callId)?.reject();

  const hangUp = () => {
    const activeCall = state.activeCall;

    if (isNil(activeCall)) {
      return;
    }

    const call = calls.get(activeCall.callId);

    if (isNil(call)) {
      // Still connecting: hang up as soon as the SDK hands the call over.
      pendingHangUps.add(activeCall.callId);
      return;
    }

    call.disconnect();
  };

  const toggleMute = () => {
    const activeCall = state.activeCall;
    const call = isNotNil(activeCall) ? calls.get(activeCall.callId) : undefined;

    if (isNil(activeCall) || isNil(call)) {
      return;
    }

    call.mute(!activeCall.isMuted);
    setState({activeCall: {...activeCall, isMuted: call.isMuted()}});
  };

  return {
    getState: () => state,
    subscribe: (listener: VoidFunction) => {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    start,
    stop,
    retryIfUnavailable,
    connect,
    accept,
    reject,
    hangUp,
    toggleMute,
  };
}

export type BrowserPhone = ReturnType<typeof createBrowserPhone>;
